import { usePlayerTranslations } from "@/i18n/player";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MediaPlayer,
  MediaProvider,
  isHLSProvider,
  type MediaPlayerInstance,
  type MediaProviderAdapter,
} from "@vidstack/react";
import {
  DefaultVideoLayout,
  defaultLayoutIcons,
} from "@vidstack/react/player/layouts/default";
import { useTranslation } from "react-i18next";
import { effectiveTransitionMs, type Clip, type Transition } from "@rec/shared";
import { clipDurationMs, locate, outputOffsets } from "@/lib/timeline";

/**
 * Preview of an unrendered timeline.
 *
 * Nothing is rendered until publish, so the preview plays the *sources*,
 * jumping between them at clip boundaries: one player, retargeted. Within one
 * source a boundary is a seek; across sources it is a src swap with a pending
 * seek applied once the new media can play. The seam is visible on a swap —
 * a beat of loading — which is the honest cost of previewing an edit that
 * does not exist yet, and publish is where it becomes seamless.
 *
 * The timeline is read through a ref on every tick, so trimming or reordering
 * mid-playback retargets on the next timeupdate rather than playing stale cuts.
 */

export type StudioPreviewHandle = {
  seekToOutput: (outputMs: number) => void;
  pause: () => void;
};

type SourceMedia = { mediaUrl: string | null; mediaType: "hls" | "mp4" };

type Props = {
  clips: Clip[];
  sources: Map<string, SourceMedia>;
  onOutputTime: (outputMs: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  /** Fill the parent's height instead of deriving height from width. */
  fill?: boolean;
};

export const StudioPreview = forwardRef<StudioPreviewHandle, Props>(
  function StudioPreview({ clips, sources, onOutputTime, onPlayingChange, fill }, ref) {
    const { t } = useTranslation(["studio", "common"]);
    const playerTranslations = usePlayerTranslations();
    const playerRef = useRef<MediaPlayerInstance | null>(null);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const clipsRef = useRef(clips);
    clipsRef.current = clips;

    /**
     * The transition overlay: the outgoing clip's last frame, frozen onto a
     * canvas and animated away while the incoming clip plays underneath.
     *
     * One player cannot blend two videos, so for a long time the preview
     * played transitions as hard cuts with a hint saying "renders on
     * publish" — technically honest, experientially "transitions don't
     * work". A frozen-frame dissolve/dip/wipe timed to the real effective
     * fade is an approximation (the outgoing side does not keep moving), but
     * it shows what was built, when it happens, for how long.
     */
    const overlayRef = useRef<HTMLCanvasElement | null>(null);
    const overlayAnim = useRef<Animation | null>(null);
    /** Clip id whose exit is currently overlaid — guards retriggering. */
    const overlayFor = useRef<string | null>(null);

    const hideOverlay = useCallback(() => {
      overlayAnim.current?.cancel();
      overlayAnim.current = null;
      overlayFor.current = null;
      const canvas = overlayRef.current;
      if (canvas) canvas.style.display = "none";
    }, []);

    const beginTransitionOverlay = useCallback(
      (clipId: string, transition: Transition, fadeMs: number) => {
        const canvas = overlayRef.current;
        const video = wrapRef.current?.querySelector("video");
        if (!canvas || !video || video.readyState < 2) return;

        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        try {
          canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch {
          // A tainted or torn-down element: skip the effect, keep the cut.
          return;
        }

        overlayFor.current = clipId;
        canvas.style.display = "block";
        canvas.style.opacity = "1";
        canvas.style.filter = "none";
        canvas.style.clipPath = "none";

        const frames: Keyframe[] =
          transition.type === "crossfade"
            ? [{ opacity: 1 }, { opacity: 0 }]
            : transition.type === "fadeblack"
              ? [
                  { opacity: 1, filter: "brightness(1)" },
                  { opacity: 1, filter: "brightness(0)", offset: 0.45 },
                  { opacity: 1, filter: "brightness(0)", offset: 0.6 },
                  { opacity: 0, filter: "brightness(0)" },
                ]
              : [
                  // wipeleft: the old picture is wiped toward the left — the
                  // right inset grows, the seam travels right-to-left,
                  // matching what ffmpeg renders.
                  { clipPath: "inset(0 0% 0 0)" },
                  { clipPath: "inset(0 100% 0 0)" },
                ];

        overlayAnim.current?.cancel();
        const anim = canvas.animate(frames, { duration: fadeMs, easing: "linear" });
        overlayAnim.current = anim;
        anim.onfinish = () => {
          if (overlayAnim.current === anim) hideOverlay();
        };
      },
      [hideOverlay],
    );

    /**
     * Whether *we* muted the player for a muted clip.
     *
     * The viewer owns the mute button; a muted clip borrows it. Tracking who
     * muted is what lets sound come back after the clip without overriding a
     * viewer who had muted the preview themselves.
     */
    const mutedByClip = useRef(false);
    const applyClipMute = useCallback((clipMuted: boolean | undefined) => {
      const player = playerRef.current;
      if (!player) return;
      if (clipMuted && !player.muted) {
        player.muted = true;
        mutedByClip.current = true;
      } else if (!clipMuted && mutedByClip.current) {
        player.muted = false;
        mutedByClip.current = false;
      }
    }, []);

    const [activeSourceId, setActiveSourceId] = useState<string | null>(
      clips[0]?.recordingId ?? null,
    );
    /** A seek to apply as soon as the (possibly new) media is ready. */
    const pendingSourceMs = useRef<number | null>(null);
    /**
     * The output position an explicit seek is settling onto. Until a
     * timeupdate lands near it, reports are stale echoes of the pre-seek
     * position and are dropped — they were snapping the editor's playhead
     * back for a beat, long enough to make "click, then S" split nothing.
     */
    const settlingToward = useRef<number | null>(null);
    const wasPlaying = useRef(false);
    const lastTick = useRef(-1);

    // First clip appearing after an empty timeline: aim the player at it.
    useEffect(() => {
      if (!activeSourceId && clips[0]) setActiveSourceId(clips[0].recordingId);
    }, [clips, activeSourceId]);

    const media = activeSourceId ? sources.get(activeSourceId) : undefined;

    const retarget = useCallback((outputMs: number, keepPlaying: boolean) => {
      const hit = locate(clipsRef.current, outputMs);
      if (!hit) return;

      wasPlaying.current = keepPlaying;
      // Stamp the intended output position: the next timeupdate resolves an
      // ambiguous source position (duplicated clips) to the copy nearest
      // this, so an explicit jump must move the reference point with it.
      lastTick.current = outputMs;
      settlingToward.current = outputMs;
      applyClipMute(hit.clip.muted);

      const player = playerRef.current;
      if (hit.clip.recordingId === (player ? activeSourceId : null) && player) {
        player.currentTime = hit.sourceMs / 1000;
        if (keepPlaying) void player.play().catch(() => {});
        return;
      }

      pendingSourceMs.current = hit.sourceMs;
      setActiveSourceId(hit.clip.recordingId);
    }, [activeSourceId, applyClipMute]);

    /**
     * Vidstack's `paused`/`pause` are getters that THROW when the underlying
     * media element is mid-teardown (a src swap, a clip deletion) — the ref
     * itself is non-null, so optional chaining does not save you. Editing
     * fired this as an uncaught TypeError on ordinary row clicks.
     */
    const isPaused = () => {
      try {
        return playerRef.current?.paused ?? true;
      } catch {
        return true;
      }
    };

    useImperativeHandle(ref, () => ({
      seekToOutput: (outputMs) => {
        // A manual jump abandons any transition mid-flight.
        hideOverlay();
        retarget(outputMs, !isPaused());
      },
      pause: () => {
        hideOverlay();
        try {
          playerRef.current?.pause();
        } catch {
          // Mid-teardown; it is not playing anyway.
        }
      },
    }));

    const handleCanPlay = useCallback(() => {
      const player = playerRef.current;
      if (!player || pendingSourceMs.current === null) return;

      player.currentTime = pendingSourceMs.current / 1000;
      pendingSourceMs.current = null;
      if (wasPlaying.current) void player.play().catch(() => {});
    }, []);

    const handleTimeUpdate = useCallback(
      (detail: { currentTime: number }) => {
        const player = playerRef.current;
        const currentClips = clipsRef.current;
        if (!player || currentClips.length === 0 || !activeSourceId) return;

        /**
         * A src swap in flight: the new media reports transient times (~0)
         * before the pending seek is applied. Mapping those would snap the
         * playhead to the new source's first clip for ~250ms — during which
         * S split "at the playhead" hit a clip boundary and silently refused.
         * Nothing here is real until the pending seek lands; ignore it all.
         */
        if (pendingSourceMs.current !== null) return;

        const sourceMs = detail.currentTime * 1000;

        // Which clip should be playing, given where the player actually is?
        // Several clips can contain the same source position — a duplicated
        // clip is the extreme case, two identical ranges — so "the first
        // match" is wrong: it mapped the second copy's playback onto the
        // first copy's output position, and the end-of-clip jump then sent
        // playback back to the second copy's start, forever. The match
        // nearest the last known output position is the one actually playing.
        const offsets = outputOffsets(currentClips);
        let containing = -1;
        let nearest = Number.POSITIVE_INFINITY;
        for (let i = 0; i < currentClips.length; i++) {
          const clip = currentClips[i]!;
          if (
            clip.recordingId === activeSourceId &&
            sourceMs >= clip.sourceStartMs - 40 &&
            sourceMs < clip.sourceEndMs
          ) {
            const candidateOutput = offsets[i]! + (sourceMs - clip.sourceStartMs);
            const distance = Math.abs(candidateOutput - lastTick.current);
            if (distance < nearest) {
              nearest = distance;
              containing = i;
            }
          }
        }

        if (containing >= 0) {
          const clip = currentClips[containing]!;
          const outputMs = offsets[containing]! + (sourceMs - clip.sourceStartMs);

          // Settling after an explicit seek: drop stale echoes of the
          // pre-seek position; accept the first report near the target.
          if (settlingToward.current !== null) {
            if (Math.abs(outputMs - settlingToward.current) > 400) return;
            settlingToward.current = null;
          }

          applyClipMute(clip.muted);
          // Report once per ~250ms of movement; the timeline draws the playhead
          // from this and does not need 60Hz.
          if (Math.abs(outputMs - lastTick.current) > 120) {
            lastTick.current = outputMs;
            onOutputTime(Math.max(0, Math.round(outputMs)));
          }

          const next = currentClips[containing + 1];
          const fadeMs = next ? effectiveTransitionMs(clip, next) : 0;

          // Entering a transition: freeze this frame over the player, jump to
          // the incoming clip (its offset already accounts for the overlap),
          // and let the overlay animate away for the fade's real duration.
          // A stale claim with no animation running (a capture that could not
          // paint) must not block this seam on the next pass through it.
          if (overlayFor.current && !overlayAnim.current) overlayFor.current = null;

          if (
            next?.transitionIn &&
            fadeMs > 0 &&
            sourceMs >= clip.sourceEndMs - fadeMs - 40
          ) {
            if (overlayFor.current !== clip.id) {
              // Claim first: the jump must happen exactly once even when the
              // frame capture fails and the overlay never shows.
              overlayFor.current = clip.id;
              beginTransitionOverlay(clip.id, next.transitionIn, fadeMs);
              retarget(offsets[containing + 1]!, true);
            }
            return;
          }

          // Approaching this clip's end: cut to the next clip.
          if (sourceMs >= clip.sourceEndMs - 60) {
            if (next) {
              retarget(offsets[containing]! + clipDurationMs(clip), true);
            } else {
              player.pause();
              onOutputTime(offsets[containing]! + clipDurationMs(clip));
            }
          }
          return;
        }

        // Mid-settle, an out-of-range position is a transient of the seek
        // itself — snapping from it is exactly the bug being suppressed.
        if (settlingToward.current !== null) return;

        // The player drifted outside every clip of this source (seek via the
        // player's own scrubber, or a trim under the playhead). Snap to the
        // start of the first clip that can claim it, or just keep quiet.
        const fallback = currentClips.findIndex(
          (c) => c.recordingId === activeSourceId,
        );
        if (fallback >= 0) {
          retarget(offsets[fallback]!, isPaused() === false);
        }
      },
      [activeSourceId, onOutputTime, retarget, applyClipMute, beginTransitionOverlay],
    );

    const handleProviderChange = useCallback(
      (provider: MediaProviderAdapter | null) => {
        if (!provider || !isHLSProvider(provider)) return;
        provider.config = {
          ...provider.config,
          // Same as the recording player: our HLS is served by the API, which
          // authenticates by cookie, and hls.js sends none unless told to.
          xhrSetup: (xhr: XMLHttpRequest) => {
            xhr.withCredentials = true;
          },
        };
      },
      [],
    );

    const src = useMemo(() => {
      if (!media?.mediaUrl) return null;
      return {
        src: media.mediaUrl,
        type:
          media.mediaType === "hls"
            ? ("application/x-mpegurl" as const)
            : ("video/mp4" as const),
      };
    }, [media]);

    if (!src) {
      return (
        <div
          className={
            fill
              ? "flex h-full items-center justify-center rounded-xl border border-border bg-black/60"
              : "flex aspect-video items-center justify-center rounded-xl border border-border bg-black/60"
          }
        >
          <p className="text-[13.5px] text-text-muted">
            {t("preview.empty")}
          </p>
        </div>
      );
    }

    return (
      <div ref={wrapRef} className={fill ? "relative h-full w-full" : "relative w-full"}>
        <MediaPlayer
          ref={playerRef}
          title={t("preview.title")}
          src={src}
          onCanPlay={handleCanPlay}
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => onPlayingChange?.(true)}
          onPause={() => onPlayingChange?.(false)}
          onProviderChange={handleProviderChange}
          onProviderSetup={handleProviderChange}
          crossOrigin
          playsInline
          preload="metadata"
          className={
            fill
              ? "h-full w-full overflow-hidden rounded-xl border border-border bg-black"
              : "w-full overflow-hidden rounded-xl border border-border bg-black"
          }
        >
          <MediaProvider />
          <DefaultVideoLayout icons={defaultLayoutIcons} translations={playerTranslations} />
        </MediaPlayer>
        {/* The frozen outgoing frame during a transition. object-contain
            letterboxes exactly like the video underneath, so the frame sits
            on the frame it froze from. */}
        <canvas
          ref={overlayRef}
          style={{ display: "none" }}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 h-full w-full rounded-xl object-contain"
        />
      </div>
    );
  },
);
